import React from 'react';
import { Card, CardContent } from '../ui/card';
import { MessageSquare, Brain, Eye, ArrowRight } from 'lucide-react';

const steps = [
  {
    number: 1,
    title: "They Reflect",
    description: "Team members use it as a thinking partner for their work challenges, wins, and growth",
    icon: MessageSquare,
    color: "from-blue-500 to-blue-600"
  },
  {
    number: 2,
    title: "System Learns",
    description: "AI extracts situations, skills demonstrated, and challenges — building a picture over time",
    icon: Brain,
    color: "from-indigo-500 to-indigo-600"
  },
  {
    number: 3,
    title: "You See",
    description: "Anonymized team health signals plus individual evidence for performance reviews",
    icon: Eye,
    color: "from-purple-500 to-purple-600"
  }
];

const TeamsHowItWorks = () => {
  return (
    <section 
      id="how-it-works"
      className="py-24 bg-gradient-to-b from-slate-950 to-slate-900"
      data-testid="teams-how-it-works"
    >
      <div className="max-w-6xl mx-auto px-6">
        {/* Section header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            How it works for your team
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            A simple flow that gives value to everyone — without adding overhead.
          </p>
        </div>

        {/* Steps */}
        <div className="relative">
          {/* Connection line (desktop) */}
          <div className="hidden lg:block absolute top-1/2 left-[16%] right-[16%] h-0.5 bg-gradient-to-r from-blue-500/50 via-indigo-500/50 to-purple-500/50 -translate-y-1/2" />
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <Card 
                  className="bg-slate-900/80 border-slate-800 hover:border-slate-700 transition-all duration-300"
                  data-testid={`step-${step.number}`}
                >
                  <CardContent className="p-8 text-center">
                    {/* Step number */}
                    <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                      <span className="text-2xl font-bold text-white">{step.number}</span>
                    </div>
                    
                    {/* Icon */}
                    <step.icon className="w-10 h-10 text-slate-400 mx-auto mb-4" />
                    
                    {/* Title */}
                    <h3 className="text-xl font-semibold text-white mb-3">
                      {step.title}
                    </h3>
                    
                    {/* Description */}
                    <p className="text-slate-400 leading-relaxed">
                      {step.description}
                    </p>
                  </CardContent>
                </Card>
                
                {/* Arrow connector (mobile) */}
                {index < steps.length - 1 && (
                  <div className="flex justify-center my-4 lg:hidden">
                    <ArrowRight className="w-6 h-6 text-slate-600 rotate-90" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Bottom tagline */}
        <div className="text-center mt-12">
          <p className="text-lg text-slate-300 italic max-w-2xl mx-auto">
            "Your team gets a thinking partner. You get visibility — 
            <span className="text-blue-400"> without becoming Big Brother.</span>"
          </p>
        </div>
      </div>
    </section>
  );
};

export default TeamsHowItWorks;
